// src/pages/GerenciarAssinatura.jsx
import React, { useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  Timestamp,
  onSnapshot
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  LuCreditCard,
  LuCalendarClock,
  LuCircleCheck,
  LuTriangleAlert,
  LuArrowRight,
  LuShieldCheck,
  LuZap,
  LuCheck,
  LuX,
  LuCircleX
} from "react-icons/lu";

import { app } from "../firebase";
import LoadingComLayout from "../components/LoadingComLayout";
import { useToast } from "../components/ToastProvider";

const db = getFirestore(app);
const auth = getAuth(app);
const functions = getFunctions(app, "us-central1");

/* ======================= ESTILOS (Padrão Configurações) ======================= */

const Container = styled.main`
  flex: 1;
  padding: 2rem;
  background-color: #f8f9fa;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr; /* Esquerda (Planos) | Direita (Status) */
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    display: flex;
    flex-direction: column-reverse; /* No mobile, planos ficam embaixo do status */
  }
`;

const Section = styled.section`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  position: relative;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background: #fff;
  
  h3 {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.1rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;

    svg { color: #3a79ed; font-size: 1.25rem; }
  }
`;

const SectionBody = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

/* === Elementos Específicos de Planos === */

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
`;

const PlanItem = styled.div`
  background: #fff;
  border: 1px solid ${({ active }) => (active ? "#3a79ed" : "#e2e8f0")};
  border-radius: 0.75rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  position: relative;
  box-shadow: ${({ active }) => (active ? "0 0 0 2px rgba(58, 121, 237, 0.1)" : "none")};

  &:hover {
    border-color: #3a79ed;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }

  .header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 0.5rem;
    h4 { margin: 0; font-size: 1rem; color: #1e293b; font-weight: 700; }
  }

  .price {
    font-size: 1.5rem; font-weight: 800; color: #0f172a;
    span { font-size: 0.85rem; color: #64748b; font-weight: 400; }
  }

  ul {
    list-style: none; padding: 0; margin: 1rem 0;
    li {
      display: flex; gap: 0.5rem; align-items: start;
      font-size: 0.85rem; color: #475569; margin-bottom: 0.5rem;
      svg { color: #3a79ed; flex-shrink: 0; margin-top: 2px; }
    }
  }
`;

/* === Elementos de UI (Iguais ao Config) === */

const InfoText = styled.p`
  font-size: 0.9rem; color: #64748b; margin: 0; line-height: 1.5;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  width: fit-content;

  ${({ variant }) => {
    switch (variant) {
      case "danger":
        return css`
          background-color: #fee2e2; color: #991b1b;
          &:hover:not(:disabled) { background-color: #fecaca; }
        `;
      case "outline":
        return css`
          background: transparent; border: 1px solid #cbd5e1; color: #475569;
          &:hover:not(:disabled) { border-color: #94a3b8; background: #f8fafc; }
        `;
      case "primary":
      default:
        return css`
          background-color: #3a79ed; color: white;
          &:hover:not(:disabled) { background-color: #2563eb; box-shadow: 0 4px 12px rgba(58, 121, 237, 0.3); }
        `;
    }
  }}
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  width: 100%; /* Full width nos cards */
`;

const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 0.25rem;
  padding: 0.25rem 0.75rem; border-radius: 99px;
  font-size: 0.85rem; font-weight: 700;
  background-color: ${({ bg }) => bg || "#f1f5f9"};
  color: ${({ color }) => color || "#475569"};
`;

const AlertBox = styled.div`
  background-color: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: start;
  gap: 0.75rem;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const DetailRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
  
  span.label { color: #64748b; font-size: 0.9rem; font-weight: 500; display: flex; gap: 0.5rem; align-items: center; }
  span.value { color: #0f172a; font-weight: 600; font-size: 0.95rem; }
`;

/* ======================= LÓGICA & DADOS ======================= */

const PLANOS = [
  { id: "single", titulo: "Single", precoLabel: "R$ 64,90", bullets: ["Agendamentos básicos", "1 barbeiro", "Suporte por WhatsApp", "Relatórios financeiros"] },
  { id: "pro", titulo: "Pro", precoLabel: "R$ 94,90", bullets: ["Agendamentos avançados", "Até 5 barbeiros adicionais", "Suporte por WhatsApp", "Relatórios financeiros"] },
  { id: "enterprise", titulo: "Enterprise", precoLabel: "R$ 149,90", bullets: ["Recursos completos", "Equipe ilimitada", "Suporte prioritário", "Relatórios financeiros"] },
];

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts instanceof Timestamp ? ts.toDate() : (typeof ts === "number" ? new Date(ts * 1000) : new Date(ts));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function diffDays(from, to) {
  const a = from instanceof Date ? from : new Date(from);
  const b = to instanceof Date ? to : new Date(to);
  const ms = b.getTime() - a.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/* ======================= COMPONENTE PRINCIPAL ======================= */

export default function GerenciarAssinatura() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [user, setUser] = useState(null);
  const [barbeariaId, setBarbeariaId] = useState(null);
  const [assinatura, setAssinatura] = useState(null);
  
  const { success, error: toastError } = useToast();

  const temAssinatura = useMemo(() => {
    if (!assinatura) return false;
    const st = (assinatura.status || "").toLowerCase();
    const hasSub = !!assinatura?._cfg?.subscriptionId;
    return hasSub || ["active", "trialing", "past_due", "unpaid"].includes(st);
  }, [assinatura]);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
      setLoading(true);
      try {
        let id = null;
        const q = query(collection(db, "barbearia"), where("criadorUid", "==", u.uid));
        const own = await getDocs(q);
        if (!own.empty) {
          id = own.docs[0].id;
        } else {
          const todas = await getDocs(collection(db, "barbearia"));
          for (const b of todas.docs) {
            const snap = await getDoc(doc(db, `barbearia/${b.id}/barbeiros/${u.uid}`));
            if (snap.exists()) { id = b.id; break; }
          }
        }
        setBarbeariaId(id);
        if (!id) { setAssinatura(null); setLoading(false); return; }

        const infoRef = doc(db, `barbearia/${id}/assinatura/info`);
        const cfgRef = doc(db, `barbearia/${id}/assinatura/config`);

        const unsubInfo = onSnapshot(infoRef, (s) => {
          if (s.exists()) {
            const d = s.data();
            setAssinatura(prev => mergeAssinatura(prev, { ...d }));
          }
        });

        const unsubCfg = onSnapshot(cfgRef, (s) => {
          if (s.exists()) {
            const c = s.data();
            setAssinatura(prev => mergeAssinatura(prev, { _cfg: c }));
          }
        });

        setLoading(false);
        return () => { unsubInfo?.(); unsubCfg?.(); };
      } catch (e) {
        console.error(e);
        toastError("Falha ao carregar assinatura.");
        setLoading(false);
      }
    });
    return () => unsubAuth();
  }, [navigate]);

  const resumo = useMemo(() => {
    if (!assinatura) return {};
    const statusRaw = (assinatura.status || "").toLowerCase();
    const now = new Date();
    const toDate = (v) => {
      if (!v) return null;
      if (v instanceof Timestamp) return v.toDate();
      if (v instanceof Date) return v;
      if (typeof v === "number") return new Date(v * 1000);
      const d = new Date(v);
      return isNaN(d) ? null : d;
    };

    const entitledUntil = toDate(assinatura.entitledUntil);
    const periodEnd = toDate(assinatura.current_period_end);
    const trialEndsLocal = toDate(assinatura.trialEndsAt) || toDate(assinatura.trial_end);
    const deadline = entitledUntil || periodEnd || trialEndsLocal;
    const planoInfo = assinatura.plano?.toLowerCase();
    const planoConfig = assinatura?._cfg?.planId?.toLowerCase();

    let planoId = planoConfig && planoInfo === 'single' && planoConfig !== 'single' 
        ? planoConfig 
        : (planoInfo || planoConfig || "—");

    let status = "Sem assinatura";
    let badge = { bg: "#f1f5f9", color: "#64748b" };
    
    if (statusRaw.includes("canceled")) {
      status = "Cancelada"; badge = { bg: "#fee2e2", color: "#991b1b" };
    } else if (statusRaw.includes("past_due") || statusRaw.includes("unpaid")) {
      status = "Pendente"; badge = { bg: "#fef3c7", color: "#92400e" };
    } else if (statusRaw.includes("trial")) {
      const dias = deadline ? diffDays(now, deadline) : 0;
      status = dias > 0 ? "Período de Teste" : "Teste expirado"; badge = { bg: "#dbeafe", color: "#1e40af" };
    } else if (statusRaw.includes("active") || statusRaw === "paid") {
      status = "Ativa"; badge = { bg: "#dcfce7", color: "#166534" };
    }

    let proxima = "—";
    if (deadline) proxima = fmtDate(deadline);
    else if (assinatura.next_payment_attempt) proxima = fmtDate(assinatura.next_payment_attempt);

    let diasRest = "—";
    if (deadline && deadline.getTime() > now.getTime()) diasRest = diffDays(now, deadline);

    return { plano: planoId.toUpperCase(), status, badge, proxima, diasRest };
  }, [assinatura]);
  
  const planoAtualId = (assinatura?.plano || assinatura?._cfg?.planId || "").toUpperCase();
  const proxPlano = (assinatura?.next_plan || "").toUpperCase();
  const inicioTroca = assinatura?.next_plan_start ?? assinatura?.current_period_end ?? null;
  const trocaAgendada = proxPlano && planoAtualId && proxPlano !== planoAtualId && inicioTroca;

  async function handleAssinar(planId) {
    if (!user) return navigate("/login");
    const newTab = window.open("about:blank", "_blank");
    try {
      setBusy(true);
      const createStripePaymentLink = httpsCallable(functions, "createStripePaymentLink");
      const { data } = await createStripePaymentLink({ planId, barbeariaId });
      if (data?.url) {
        if (newTab) newTab.location = data.url;
      } else throw new Error("Erro ao gerar link.");
    } catch (e) {
      toastError(e.message);
      if(newTab) newTab.close();
    } finally { setBusy(false); }
  }

  async function handleTrocarPlano(newPlanId) {
    if (!user) return navigate("/login");
    try {
      setBusy(true);
      const changeStripePlan = httpsCallable(functions, "changeStripePlan");
      const { data } = await changeStripePlan({ newPlanId, barbeariaId });
      if (data?.portalUrl) {
        success(`Plano alterado. Finalize no portal.`);
        window.location.assign(data.portalUrl);
        return;
      }
      success(`Plano alterado para ${newPlanId.toUpperCase()}.`);
    } catch (e) { console.error(e); toastError("Erro ao trocar plano."); } finally { setBusy(false); }
  }

  async function handleMigrarOuAssinar(planId) {
    if (temAssinatura) return handleTrocarPlano(planId);
    return handleAssinar(planId);
  }

  async function handleCancelar(immediate = false) {
    if (!window.confirm(immediate ? "Cancelar IMEDIATAMENTE?" : "Deseja cancelar ao fim do ciclo atual?")) return;
    try {
      setBusy(true);
      const cancelar = httpsCallable(functions, "cancelStripeSubscription");
      await cancelar({ barbeariaId, immediate });
      success(immediate ? "Cancelada imediatamente." : "Agendado para o fim do ciclo.");
    } catch (e) { toastError(e.message); } finally { setBusy(false); }
  }

  function mergeAssinatura(prev, patch) {
    return { ...prev, _cfg: prev?._cfg, ...patch };
  }

  if (loading) return <LoadingComLayout />;

  return (
    <>
      <Container>
        
        {/* PAGE GRID: ESQUERDA (PLANOS) | DIREITA (STATUS/AÇÕES) */}
        <PageGrid>
          
          {/* === COLUNA PRINCIPAL: SELEÇÃO DE PLANOS === */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Section>
              <SectionHeader>
                <h3><LuZap /> Planos Disponíveis</h3>
              </SectionHeader>
              <SectionBody>
                <InfoText style={{ marginBottom: '1rem' }}>
                  Escolha o plano ideal para o tamanho da sua barbearia. Altere quando quiser.
                </InfoText>

                <PlansGrid>
                  {PLANOS.map((p) => {
                    const ativo = (resumo.plano || "").toLowerCase() === p.id;
                    return (
                      <PlanItem key={p.id} active={ativo}>
                        <div className="header">
                          <h4>{p.titulo}</h4>
                          {ativo && <Badge bg="#3a79ed" color="#fff">Atual</Badge>}
                        </div>
                        
                        <div className="price">
                          {p.precoLabel} <span>/mês</span>
                        </div>

                        <ul>
                          {p.bullets.map((b, i) => (
                            <li key={i}><LuCheck strokeWidth={3} /> {b}</li>
                          ))}
                        </ul>

                        <Button 
                          variant={ativo ? "outline" : "primary"}
                          onClick={() => handleMigrarOuAssinar(p.id)}
                          disabled={busy || ativo}
                          style={{ marginTop: 'auto' }}
                        >
                          {ativo ? "Seu Plano" : temAssinatura ? "Mudar para este" : "Assinar Agora"}
                          {!ativo && <LuArrowRight />}
                        </Button>
                      </PlanItem>
                    );
                  })}
                </PlansGrid>
              </SectionBody>
            </Section>
          </div>

          {/* === COLUNA LATERAL: STATUS E AÇÕES === */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            
            <Section>
              <SectionHeader>
                <h3><LuCreditCard /> Assinatura</h3>
              </SectionHeader>
              <SectionBody>
                
                {/* Info do Status */}
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <Badge bg={resumo.badge?.bg} color={resumo.badge?.color} style={{ fontSize: '0.9rem', padding: '0.4rem 1rem' }}>
                    {resumo.status}
                  </Badge>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', marginTop: '0.75rem' }}>
                    {temAssinatura ? `Plano ${resumo.plano}` : "Gratuito / Sem Plano"}
                  </div>
                </div>

                {/* Detalhes */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <DetailRow>
                    <span className="label"><LuShieldCheck size={16} /> Plano</span>
                    <span className="value">{resumo.plano}</span>
                  </DetailRow>
                  <DetailRow>
                    <span className="label"><LuCalendarClock size={16} /> {resumo.diasRest !== '—' ? 'Renova em' : 'Vencimento'}</span>
                    <span className="value">{resumo.proxima}</span>
                  </DetailRow>
                  {resumo.diasRest !== '—' && (
                    <DetailRow>
                       <span className="label">Dias restantes</span>
                       <span className="value">{resumo.diasRest} dias</span>
                    </DetailRow>
                  )}
                </div>

              </SectionBody>
            </Section>

             {/* Aviso de Troca (Se houver) */}
            {trocaAgendada && (
              <AlertBox>
                <LuTriangleAlert size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <strong>Troca Agendada:</strong><br/>
                  Mudança para <b>{proxPlano}</b> em {fmtDate(inicioTroca)}.
                </div>
              </AlertBox>
            )}

            {/* Botão de Cancelar (Zona de Perigo Style) */}
            {temAssinatura && (
               <Section>
                 <SectionHeader>
                    <h3><LuCircleX/> Cancelar Serviços</h3>
                 </SectionHeader>
                 <SectionBody style={{ padding: '1rem' }}>
                   <InfoText style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                     Ao cancelar, você perderá acesso aos recursos premium ao fim do ciclo atual.
                   </InfoText>
                   <Button 
                     variant="danger" 
                     onClick={() => handleCancelar(false)}
                     disabled={busy}
                   >
                     <LuX /> Cancelar Assinatura
                   </Button>
                 </SectionBody>
               </Section>
            )}

          </div>

        </PageGrid>
      </Container>
    </>
  );
}